import { useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

export const REFRESH_INTERVAL_SECONDS = 60

interface RefreshContextValue {
  enabled: boolean
  countdown: number
  /** True while a dialog is open; the periodic refresh waits until it closes. */
  paused: boolean
  lastRefresh: Date | null
  refreshNow: () => void
  toggleEnabled: () => void
  pause: () => void
  resume: () => void
}

const RefreshContext = createContext<RefreshContextValue | null>(null)

/**
 * Drives the one-minute auto refresh of the whole dashboard. Widgets do not poll on
 * their own: a tick invalidates every active query, so all data refreshes together.
 */
export function RefreshProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [enabled, setEnabled] = useState(true)
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL_SECONDS)
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null)
  const [blockers, setBlockers] = useState(0)
  const paused = blockers > 0
  const pausedRef = useRef(paused)
  useEffect(() => {
    pausedRef.current = paused
  }, [paused])

  const refreshNow = useCallback(() => {
    void queryClient.invalidateQueries({ type: 'active' })
    setLastRefresh(new Date())
    setCountdown(REFRESH_INTERVAL_SECONDS)
  }, [queryClient])

  useEffect(() => {
    if (!enabled) return
    const handle = setInterval(() => {
      if (pausedRef.current) return
      setCountdown((current) => {
        if (current > 1) return current - 1
        refreshNow()
        return REFRESH_INTERVAL_SECONDS
      })
    }, 1000)
    return () => clearInterval(handle)
  }, [enabled, refreshNow])

  const value = useMemo<RefreshContextValue>(
    () => ({
      enabled,
      countdown,
      paused,
      lastRefresh,
      refreshNow,
      toggleEnabled: () => {
        setEnabled((e) => !e)
        setCountdown(REFRESH_INTERVAL_SECONDS)
      },
      pause: () => setBlockers((b) => b + 1),
      resume: () => setBlockers((b) => Math.max(0, b - 1)),
    }),
    [enabled, countdown, paused, lastRefresh, refreshNow],
  )

  return <RefreshContext.Provider value={value}>{children}</RefreshContext.Provider>
}

export function useRefresh(): RefreshContextValue {
  const context = useContext(RefreshContext)
  if (!context) throw new Error('useRefresh must be used inside RefreshProvider')
  return context
}

/** Pauses the auto refresh while the calling component (a dialog) is mounted. */
export function useRefreshPause(): void {
  const { pause, resume } = useRefresh()
  useEffect(() => {
    pause()
    return resume
  }, [pause, resume])
}
